/**
 * EN-1883: an admin sets how long a new user stays active, and can
 * deactivate, reactivate or extend a user. Pure helpers for the user
 * management screens and the user pickers; e2e/user-expiry.unit.spec.ts
 * covers them. The API contract is in apex-agm-v1 docs/user-expiry.md.
 */

import {
  NotificationUserOption,
  sortNotificationUserOptions,
  toNotificationUserOption,
} from './SmsOptIn';

/** The `activeFor` values the API accepts. */
export type ActiveFor = '24h' | '3d' | '7d' | 'forever';
/** Edit only: leave the user's expiry as it is (activeFor is not sent). */
export const ACTIVE_FOR_UNCHANGED = 'unchanged';
export type ActiveForChoice = ActiveFor | typeof ACTIVE_FOR_UNCHANGED;

export interface ActiveForOption {
  label: string;
  value: ActiveForChoice;
}

export const ACTIVE_FOR_OPTIONS: ActiveForOption[] = [
  { label: '24 hours', value: '24h' },
  { label: '3 days', value: '3d' },
  { label: '7 days', value: '7d' },
  { label: 'Forever', value: 'forever' },
];
/** Default on create (the API's default when activeFor is omitted). */
export const DEFAULT_ACTIVE_FOR: ActiveFor = 'forever';

/** The fields of a user these helpers read. */
export interface UserExpiryFields {
  expiresAtUtc?: string | null;
  deactivatedAtUtc?: string | null;
  isActive?: boolean | null;
}

/** "Active for" options: create lists the periods, edit adds "Unchanged". */
export function activeForOptions(isEdit: boolean): ActiveForOption[] {
  const options = ACTIVE_FOR_OPTIONS.map((option) => ({ ...option }));
  return isEdit
    ? [{ label: 'Unchanged', value: ACTIVE_FOR_UNCHANGED }, ...options]
    : options;
}

/** The activeFor to send for a choice, or undefined to leave it out. */
export function activeForToSend(
  choice: ActiveForChoice | string | null | undefined
): ActiveFor | undefined {
  const option = ACTIVE_FOR_OPTIONS.find((o) => o.value === choice);
  return option ? (option.value as ActiveFor) : undefined;
}

export type UserExpiryChipKind = 'deactivated' | 'expired' | 'expires';

export interface UserExpiryChip {
  kind: UserExpiryChipKind;
  label: string;
  color: string;
}

/**
 * Milliseconds of an API timestamp (NaN when unparseable). The API always
 * sends UTC with a Z; a timestamp without an offset is read as UTC too,
 * never as browser local time (review N7).
 */
export function parseUtcTimestamp(iso: string): number {
  const text = typeof iso === 'string' ? iso.trim() : '';
  const hasOffset = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(text);
  return Date.parse(text.includes('T') && !hasOffset ? `${text}Z` : text);
}

/** An expiry timestamp in the browser's local time zone. */
export function formatExpiryLocal(isoUtc: string): string {
  const date = new Date(parseUtcTimestamp(isoUtc));
  if (Number.isNaN(date.getTime())) return isoUtc;
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/**
 * The list chip for a user: Deactivated, Expired (from the server's
 * isActive, never the browser clock), "Expires <local time>", or null.
 */
export function userExpiryChip(
  user: UserExpiryFields | null | undefined,
  formatLocal: (isoUtc: string) => string = formatExpiryLocal
): UserExpiryChip | null {
  if (!user) return null;
  if (user.deactivatedAtUtc != null)
    return { kind: 'deactivated', label: 'Deactivated', color: 'grey-7' };
  if (user.isActive === false)
    return { kind: 'expired', label: 'Expired', color: 'negative' };
  if (user.expiresAtUtc != null)
    return {
      kind: 'expires',
      label: `Expires ${formatLocal(user.expiresAtUtc)}`,
      color: 'orange-8',
    };
  return null;
}

/**
 * The chip of every listed user by id, computed once per list change
 * (review N6), so a row reads its chip instead of computing it per binding.
 */
export function expiryChipsById(
  users:
    | readonly (UserExpiryFields & { id?: string | null })[]
    | null
    | undefined,
  formatLocal: (isoUtc: string) => string = formatExpiryLocal
): Record<string, UserExpiryChip | null> {
  const chips: Record<string, UserExpiryChip | null> = {};
  for (const user of users || [])
    if (user && user.id) chips[user.id] = userExpiryChip(user, formatLocal);
  return chips;
}

export const REACTIVATABLE_DUPLICATE_MESSAGE =
  'A deactivated user with this email already exists. Reactivate them instead.';

export const USER_EXPIRY_ERROR_MESSAGES: Record<string, string> = {
  invalid_active_for:
    'Choose an active period: 24 hours, 3 days, 7 days or Forever.',
  active_for_required:
    "This user's active period has ended. Choose a new active period to reactivate them.",
  user_has_no_expiry:
    'This user has no expiry, so it cannot be extended. Edit the user to set an active period.',
  cannot_deactivate_self:
    'You cannot deactivate, reactivate or extend your own account.',
  last_active_admin:
    "This is the last active admin of the group, so they can't be deactivated or given a limited active period. Make another user an admin first.",
  protected_account:
    'This account is managed by ProPipe and cannot be changed here.',
  cannot_change_own_active_period: 'You cannot change your own active period.',
  // the DB change may be saved but not yet applied in Auth0; repeating the
  // same call finishes it (contract: "retry the same call")
  auth0_unavailable:
    'The sign-in service could not be reached. Please try the same action again.',
  concurrent_admin_change:
    'Another administrator changed this user at the same time. Please try again.',
  // reactivate / extend saved the DB change; the list is re-fetched
  auth0_user_not_found:
    'Saved, but this user has no sign-in account and cannot sign in. Contact ProPipe support.',
};

/** A 403 without a code: not an admin, no group, or another group's user. */
export const PERMISSION_DENIED_MESSAGE =
  "You don't have permission to change this user.";
/** A 404: the user is gone (the list is re-fetched). */
export const USER_NOT_FOUND_MESSAGE = 'This user no longer exists.';

interface ErrorResponse {
  status?: number;
  data?: unknown;
}

function errorResponse(error: unknown): ErrorResponse | undefined {
  return (error as { response?: ErrorResponse } | null | undefined)?.response;
}

/**
 * The message to show for a failed user management call: the mapped text
 * for an EN-1883 error code, the reactivatable duplicate 409, otherwise the
 * API's message (or plain-text body), otherwise `fallback`.
 */
export function userExpiryErrorMessage(
  error: unknown,
  fallback: string
): string {
  if (reactivatableUserId(error)) return REACTIVATABLE_DUPLICATE_MESSAGE;
  const response = errorResponse(error);
  const data = response?.data;
  const code = errorCode(error);
  if (
    code !== null &&
    Object.prototype.hasOwnProperty.call(USER_EXPIRY_ERROR_MESSAGES, code)
  )
    return USER_EXPIRY_ERROR_MESSAGES[code];
  // bodiless 403 / 404 (review S4): a retry cannot help
  if (response?.status === 403 && code === null)
    return PERMISSION_DENIED_MESSAGE;
  if (response?.status === 404) return USER_NOT_FOUND_MESSAGE;
  if (typeof data === 'string') return data || fallback;
  const body = data as { message?: unknown } | null | undefined;
  if (typeof body?.message === 'string' && body.message) return body.message;
  return fallback;
}

/**
 * True when the list should be re-fetched after a failed user action: 404
 * (the user is gone) or 409 auth0_user_not_found (the DB change was saved).
 */
export function userActionNeedsRefresh(error: unknown): boolean {
  const status = errorResponse(error)?.status;
  return (
    status === 404 ||
    (status === 409 && errorCode(error) === 'auth0_user_not_found')
  );
}

function errorCode(error: unknown): string | null {
  const data = errorResponse(error)?.data as
    | { code?: unknown }
    | string
    | null
    | undefined;
  const code = typeof data === 'object' && data ? data.code : undefined;
  return typeof code === 'string' && code ? code : null;
}

/** The userId of a 409 reactivatable duplicate, otherwise null. */
export function reactivatableUserId(error: unknown): string | null {
  const response = errorResponse(error);
  if (response?.status !== 409) return null;
  const body = response.data as
    | { reactivatable?: unknown; userId?: unknown }
    | null
    | undefined;
  return body?.reactivatable === true &&
    typeof body.userId === 'string' &&
    body.userId
    ? body.userId
    : null;
}

/** Users that are not inactive (isActive !== false), in the input order. */
export function activeUsers<T extends UserExpiryFields>(
  users: readonly T[] | null | undefined
): T[] {
  return users ? users.filter((user) => user.isActive !== false) : [];
}

/** The fields the user management actions read. */
export interface UserActionFields extends UserExpiryFields {
  id?: string | null;
  name?: string | null;
}

/** Which of Deactivate / Reactivate / Extend a user row offers. */
export interface UserRowActions {
  deactivate: boolean;
  reactivate: boolean;
  extend: boolean;
}

/** True when `user` is the signed-in admin (their own row). */
export function isSelf(
  user: UserActionFields | null | undefined,
  currentUserId: string | null | undefined
): boolean {
  return !!user && !!user.id && !!currentUserId && user.id === currentUserId;
}

/**
 * Deactivate for an active user, Reactivate for every user (on an active
 * row it re-applies the sign-in unblock after a half-failed reactivate,
 * review S2), Extend only for an active user with an expiry; nothing on
 * the admin's own row (the API refuses all three for yourself).
 */
export function userRowActions(
  user: UserActionFields | null | undefined,
  currentUserId: string | null | undefined
): UserRowActions {
  if (!user || !user.id || isSelf(user, currentUserId))
    return { deactivate: false, reactivate: false, extend: false };
  const inactive = user.deactivatedAtUtc != null || user.isActive === false;
  return {
    deactivate: !inactive,
    reactivate: true,
    extend: !inactive && user.expiresAtUtc != null,
  };
}

/**
 * The Reactivate button's label: "Reactivate" for an inactive user; on an
 * active row it re-applies the sign-in unblock after a half-failed
 * reactivate (review S2).
 */
export function reactivateActionLabel(
  user: UserActionFields | null | undefined
): string {
  return isInactive(user) ? 'Reactivate' : 'Reactivate / restore access';
}

/** No user (unknown), deactivated, or expired by the server. */
function isInactive(user: UserActionFields | null | undefined): boolean {
  return !user || user.deactivatedAtUtc != null || user.isActive === false;
}

/**
 * Reactivate must send a period when the user is expired or the expiry is
 * already past (otherwise the API answers 400 active_for_required). An
 * unknown user (null) must pick one too.
 */
export function reactivateNeedsPeriod(
  user: UserActionFields | null | undefined,
  now: number = Date.now()
): boolean {
  if (!user) return true;
  if (user.deactivatedAtUtc == null && user.isActive === false) return true;
  if (user.expiresAtUtc == null) return false;
  const expires = parseUtcTimestamp(user.expiresAtUtc);
  return Number.isNaN(expires) || expires <= now;
}

/** A Quasar Dialog plugin options object ($q.dialog). */
export interface ConfirmDialogOptions {
  title: string;
  message: string;
  cancel: boolean;
  persistent: boolean;
  ok: { label: string; color: string; noCaps: boolean };
  options?: {
    type: 'radio';
    model: string;
    items: { label: string; value: string }[];
  };
}

function displayName(user: UserActionFields | null | undefined): string {
  return (user && user.name) || 'this user';
}

function periodItems(): { label: string; value: string }[] {
  return ACTIVE_FOR_OPTIONS.map((o) => ({ label: o.label, value: o.value }));
}

/** Confirm Deactivate. */
export function deactivateDialog(
  user: UserActionFields | null | undefined
): ConfirmDialogOptions {
  return {
    title: 'Deactivate user',
    message: `Deactivate ${displayName(
      user
    )}? They will not be able to sign in to ProPipe until they are reactivated. Nothing is deleted.`,
    cancel: true,
    persistent: true,
    ok: { label: 'Deactivate', color: 'negative', noCaps: true },
  };
}

/**
 * Confirm Reactivate, with a period choice. "Keep the current period" is
 * offered only when the API would accept a reactivate without one; an
 * unknown user (the 409 duplicate of another group) must pick a period.
 */
export function reactivateDialog(
  user: UserActionFields | null | undefined,
  now: number = Date.now()
): ConfirmDialogOptions {
  const needsPeriod = reactivateNeedsPeriod(user, now);
  const restore = !isInactive(user);
  const name = displayName(user);
  return {
    title: restore ? 'Reactivate / restore access' : 'Reactivate user',
    message: restore
      ? `${name} is already active. Reactivate again if they still cannot sign in (for example after the sign-in service could not be reached).${
          needsPeriod
            ? ' Their active period has ended, so choose a new one.'
            : ''
        }`
      : needsPeriod
      ? `Reactivate ${name}? Their active period has ended, so choose how long they stay active.`
      : `Reactivate ${name}? They can sign in again.`,
    cancel: true,
    persistent: true,
    ok: { label: 'Reactivate', color: 'primary', noCaps: true },
    options: {
      type: 'radio',
      model: needsPeriod ? DEFAULT_ACTIVE_FOR : ACTIVE_FOR_UNCHANGED,
      items: needsPeriod
        ? periodItems()
        : [
            { label: 'Keep the current period', value: ACTIVE_FOR_UNCHANGED },
            ...periodItems(),
          ],
    },
  };
}

/** Confirm Extend, with the period to add. */
export function extendDialog(
  user: UserActionFields | null | undefined
): ConfirmDialogOptions {
  return {
    title: 'Extend user',
    message: `Extend ${displayName(
      user
    )}'s active period by (from their current expiry, or from now if it has passed):`,
    cancel: true,
    persistent: true,
    ok: { label: 'Extend', color: 'primary', noCaps: true },
    options: { type: 'radio', model: '24h', items: periodItems() },
  };
}

/**
 * A notification user option (EN-15: label with the SMS opt-in status, plain
 * name, smsOptIn) plus isActive, so pickers can hide inactive users.
 */
export interface UserPickerOption extends NotificationUserOption {
  isActive: boolean;
}

/**
 * Every user as a picker option, sorted by name. Inactive users stay in the
 * list (existing selections still show their names and SMS opt-in status);
 * pass the list through activeUsers() for the options a picker offers.
 */
export function userPickerOptions(
  users: readonly any[] | null | undefined
): UserPickerOption[] {
  if (!users) return [];
  // EN-15's option and order (by name), plus isActive
  const options: UserPickerOption[] = users.map((user) => ({
    ...toNotificationUserOption({
      id: user?.id,
      name: typeof user?.name === 'string' ? user.name : '',
      a2pMessagingOptInState: user?.a2pMessagingOptInState,
    }),
    isActive: user?.isActive !== false,
  }));
  return sortNotificationUserOptions(options) as UserPickerOption[];
}
